import { useCallback, useEffect, useRef, useState } from "react";

/* ============================================================================
   useAutosave — autoguardado por sección

   - Dispara `save(value)` cuando `serialize(value)` cambia respecto a lo último
     confirmado como guardado. NUNCA manda una petición idéntica.
   - Debounce configurable; la petición anterior de la MISMA sección se cancela
     (se ignora su resultado) cuando llega un cambio nuevo.
   - Si `save` falla, el estado pasa a "error" y el valor NO se toca: el texto
     escrito no se pierde. El reintento es explícito (`retry()`).
   - `flush()` fuerza el guardado pendiente inmediatamente (p. ej. al pulsar
     "Guardar y salir" o "Finalizar").

   `save(value)` debe devolver un objeto con `.success === true` cuando guardó,
   o un valor falsy / `{success:false}` cuando no. Si además devuelve `.data`,
   se entrega a `onSaved(data)` para que la sección adopte ids nuevos.

   estado: "idle" | "dirty" | "saving" | "saved" | "error"
   ========================================================================== */
export default function useAutosave({
  value,
  save,
  serialize = JSON.stringify,
  delay = 900,
  enabled = true,
  onSaved,
}) {
  const [status, setStatus] = useState("idle");
  const [savedAt, setSavedAt] = useState(null);

  const baselineRef = useRef(serialize(value)); // lo último confirmado guardado
  const timerRef = useRef(null);
  const runIdRef = useRef(0); // token de cancelación
  const valueRef = useRef(value);
  const saveRef = useRef(save);
  const onSavedRef = useRef(onSaved);

  valueRef.current = value;
  saveRef.current = save;
  onSavedRef.current = onSaved;

  const doSave = useCallback(async () => {
    const current = valueRef.current;
    const snapshot = serialize(current);
    if (snapshot === baselineRef.current) {
      setStatus((s) => (s === "saving" ? "saved" : s));
      return;
    }
    // Empezó a guardar: si mientras tanto entra otro cambio, este run se ignora.
    const runId = ++runIdRef.current;
    setStatus("saving");
    let res;
    try {
      res = await saveRef.current(current);
    } catch (e) {
      res = null;
    }
    if (runId !== runIdRef.current) return; // llegó tarde: hubo un cambio nuevo
    if (res && res.success) {
      baselineRef.current = snapshot;
      setSavedAt(new Date());
      setStatus("saved");
      if (res.data !== undefined && onSavedRef.current) onSavedRef.current(res.data);
    } else {
      setStatus("error");
    }
  }, [serialize]);

  // Marca "dirty" y agenda el guardado cuando el contenido cambia.
  useEffect(() => {
    if (!enabled) return;
    const snapshot = serialize(value);
    if (snapshot === baselineRef.current) return;
    setStatus("dirty");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(doSave, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialize(value), enabled, delay]);

  const retry = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    doSave();
  }, [doSave]);

  const flush = useCallback(async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    await doSave();
  }, [doSave]);

  // Permite que el orquestador reajuste la línea base tras un refresco global
  // (p. ej. se recargó la cita entera): lo ya persistido no debe re-mandarse.
  const syncBaseline = useCallback(
    (nextValue) => {
      baselineRef.current = serialize(nextValue === undefined ? valueRef.current : nextValue);
      setStatus("idle");
    },
    [serialize]
  );

  return {
    status,
    savedAt,
    retry,
    flush,
    syncBaseline,
    isDirty: status === "dirty" || status === "saving" || status === "error",
  };
}
